import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useInView } from 'framer-motion';

// ─────────────────────────────────────────────────────────────
// CountUp — animate a number from 0 → target when in view
// ─────────────────────────────────────────────────────────────
export const CountUp = ({ value, suffix = '', duration = 2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [display, setDisplay] = useState(0);

  // Parse "6+" → 6, "15+" → 15, "3" → 3
  const target = parseInt(String(value).replace(/[^0-9]/g, ''), 10) || 0;
  const computedSuffix = String(value).replace(/[0-9]/g, '') || suffix;

  useEffect(() => {
    if (!inView) return;
    let startTime;
    let raf;
    const step = (now) => {
      if (!startTime) startTime = now;
      const progress = Math.min((now - startTime) / (duration * 1000), 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  return <span ref={ref}>{display}{computedSuffix}</span>;
};

// ─────────────────────────────────────────────────────────────
// MagneticButton — wraps a child, pulls toward cursor
// ─────────────────────────────────────────────────────────────
export const Magnetic = ({ children, strength = 0.25, className = '' }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 18, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 18, mass: 0.4 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * strength);
    y.set((e.clientY - rect.top - rect.height / 2) * strength);
  };
  const handleLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x: sx, y: sy }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────
// WordsReveal — splits headline into words, staggers them in
// ─────────────────────────────────────────────────────────────
export const WordsReveal = ({ text, className = '', delay = 0, as: Tag = 'span' }) => {
  const words = text.split(' ');
  return (
    <Tag className={className}>
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom" style={{ paddingBottom: '0.1em' }}>
          <motion.span
            className="inline-block"
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, delay: delay + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
          >
            {word}{i < words.length - 1 ? '\u00A0' : ''}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// ─────────────────────────────────────────────────────────────
// Float — gentle continuous up/down hover
// ─────────────────────────────────────────────────────────────
export const Float = ({ children, distance = 8, duration = 4, delay = 0, className = '' }) => (
  <motion.div
    animate={{ y: [-distance, distance, -distance] }}
    transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

// ─────────────────────────────────────────────────────────────
// MaskReveal — image / block uncovered by sliding gold mask
// ─────────────────────────────────────────────────────────────
export const MaskReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: delay + 0.5 }}
      >
        {children}
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-[#C9A227]"
        initial={{ x: '-101%' }}
        animate={inView ? { x: ['-101%', '0%', '101%'] } : {}}
        transition={{ duration: 1.2, delay, times: [0, 0.5, 1], ease: [0.65, 0, 0.35, 1] }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Shimmer — gold sheen sweeps across a button on hover
// ─────────────────────────────────────────────────────────────
export const Shimmer = ({ children, className = '' }) => (
  <span className={`relative overflow-hidden inline-flex items-center justify-center group ${className}`}>
    {children}
    <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent" />
  </span>
);
